import React from "react";
import { styled } from "@mui/system";
import { useIsMobile } from "src/utils/hooks/useIsMobile";

const getGradientBackground = (tier: "bronze" | "silver" | "gold" | "black", str: string) => {
  switch (tier) {
    case "bronze":
      return `radial-gradient(85.74% 85.74% at 80.5% 50%, rgba(51, 31, 0, ${str}) 0%, rgba(238, 241, 245, ${str}) 100%)`;
    case "silver":
      return `radial-gradient(85.74% 85.74% at 80.5% 50%, rgba(192,193, 192, ${str}) 0%, rgba(238, 241, 245, ${str}) 100%)`;
    case "gold":
      return `radial-gradient(85.74% 85.74% at 80.5% 50%, rgba(212, 175, 55, ${str}) 0%, rgba(238, 241, 245, ${str}) 100%)`;
    case "black":
      return `radial-gradient(91.19% 91.19% at 89.5% 44%, rgba(0, 0, 0, ${str}) 28.9%, rgba(229, 225, 215, ${str}) 83.5%)`;
    default:
      return "";
  }
};

interface MedalWrapperProps {
  background: string;
  scale?: string;
}
const MedalWrapper = styled("div")<MedalWrapperProps>(({ background, scale }) => ({
  display: "flex",
  width: "32px",
  height: "32px",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  flexShrink: 0,
  borderRadius: "88.889px",
  cursor: "pointer",
  background: background,
  transform: `scale(${scale})`,
}));

interface InnerCircleProps {
  filledBackground?: boolean;
  defaultBackground?: boolean;
}
const InnerCircle = styled("div")<InnerCircleProps>(({ filledBackground, defaultBackground }) => ({
  display: "flex",
  width: "25.6px",
  height: "25.6px",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  gap: "1.28px",
  flexShrink: 0,
  borderRadius: "25.6px",
  background: filledBackground
    ? "rgba(248, 249, 249, 0.6)"
    : defaultBackground
    ? "rgba(248, 249, 249, 0.70)"
    : "#FFF",

  "&:hover": {
    background: "rgba(248, 249, 249, 0.6)",
  },
}));

const Text = styled("div")(() => ({
  display: "flex",
  alignitems: "center",
  color: "#002035",
  fontFamily: "Söhne",
  fontSize: "12.444px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "15px",
}));

const NotificationCircleWrapper = styled("div")(() => ({
  position: "relative",
  top: 0,
  left: 0,
}));
interface NotificationCircleProps {
  notification: boolean;
}
const NotificationCircle = styled("div")<NotificationCircleProps>(({ notification }) => ({
  width: 14,
  height: 14,
  position: "absolute",
  left: 5,
  bottom: 16,
  borderRadius: "8.889px",
  background: "#FC4C4C",
  visibility: notification ? "visible" : "hidden",
}));

const AccountLockIconWrapper = styled("div")(() => ({
  position: "relative",
  top: 18,
  right: 0,
}));
const AccountLockCircle = styled("div")(() => ({
  width: 14,
  height: 14,
  position: "absolute",
  left: 5,
  bottom: 16,
  borderRadius: "6.667px",
  background: "#fff",
}));
const LockIcon = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 15 17" fill="none">
      <g clip-path="url(#clip0_1227_9223)">
        <path
          d="M9.11111 4.22226H8.60318V3.20639C8.60318 1.80448 7.4654 0.666706 6.06349 0.666706C4.66159 0.666706 3.52381 1.80448 3.52381 3.20639V4.22226H3.01587C2.45714 4.22226 2 4.6794 2 5.23813V10.3175C2 10.8762 2.45714 11.3334 3.01587 11.3334H9.11111C9.66984 11.3334 10.127 10.8762 10.127 10.3175V5.23813C10.127 4.6794 9.66984 4.22226 9.11111 4.22226ZM6.06349 8.79369C5.50476 8.79369 5.04762 8.33655 5.04762 7.77782C5.04762 7.21909 5.50476 6.76194 6.06349 6.76194C6.62222 6.76194 7.07937 7.21909 7.07937 7.77782C7.07937 8.33655 6.62222 8.79369 6.06349 8.79369ZM7.6381 4.22226H4.48889V3.20639C4.48889 2.33782 5.19492 1.63179 6.06349 1.63179C6.93206 1.63179 7.6381 2.33782 7.6381 3.20639V4.22226Z"
          fill="#9CA3A8"
        />
      </g>
      <defs>
        <clipPath id="clip0_1227_9223">
          <rect width="20" height="20" fill="white" transform="translate(0.666504 0.666666)" />
        </clipPath>
      </defs>
    </svg>
  );
};

interface ProfileInitialsProps {
  initials: string;
  notification?: boolean;
  tier: "bronze" | "silver" | "gold" | "black";
  accountLocked: boolean;
  filled?: boolean;
  scale?: string;
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
}

/**
 * A component to display profile initials with optional notification indicator and tier styling.
 * @param {string} props.initials - The initials to display.
 * @param {boolean} [props.notification=false] - Whether to display a notification indicator (default is false).
 * @param {"bronze" | "silver" | "gold" | "black"} props.tier - The tier of the profile (bronze, silver, gold, or black).
 * @param {boolean} [props.filled=false] - Whether the inner circle should be filled (default is false).
 * @param {string} [props.scale] - The scale of the component.
 * @param {function} [props.onClick] - The function to run when user clicks on component (optional).
 */
const ProfileInitials = (props: ProfileInitialsProps) => {
  const { initials, notification, tier, accountLocked, filled, scale, onClick } = props;
  const isMobile = useIsMobile();

  let background = "";
  let strength = "";
  let isDefault = false;

  switch (tier.toLowerCase()) {
    case "bronze":
      strength = isMobile ? "1" : "0.65";
      background = getGradientBackground("bronze", strength);
      break;
    case "silver":
      strength = isMobile ? "1" : "0.26";
      background = getGradientBackground("silver", strength);
      break;
    case "gold":
      strength = isMobile ? "1" : "0.65";
      background = getGradientBackground("gold", strength);
      break;
    case "black":
      strength = isMobile ? "1" : "0.50";
      background = getGradientBackground("black", strength);
      break;
    default:
      background = "#002035";
      isDefault = true;
      break;
  }

  return (
    <MedalWrapper background={background} scale={scale} onClick={onClick}>
      <InnerCircle filledBackground={filled} defaultBackground={isDefault}>
        <Text>{initials}</Text>
      </InnerCircle>

      <NotificationCircleWrapper>
        <NotificationCircle notification={notification ?? false} />
      </NotificationCircleWrapper>

      {accountLocked && (
        <AccountLockIconWrapper>
          <AccountLockCircle>
            <LockIcon />
          </AccountLockCircle>
        </AccountLockIconWrapper>
      )}
    </MedalWrapper>
  );
};

export default ProfileInitials;
